"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { MessageCircle, X } from "lucide-react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { ChatWindow } from "./chat-window";
import { ChatAttachment, ChatMessageProps } from "./chat-message";
import { cn } from "@/lib/utils";
import { useChatContext } from "@/components/aios-assistant/context";
import { serializeCatalog } from "@/utils/route-catalog";
import { useQueryClient } from "@tanstack/react-query";
import { postStream, del, fetchData } from "@/utils/api-handler";
import { toast } from "sonner";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { useProtectedQuery } from "@/hooks/use-protected-query";

// ─── Action types the backend tool-runner can return ──────────────────────────
//
// Backend handles ALL data mutations via its own tools.
// Frontend only needs to:
//   1. Navigate to a page/tab
//   2. Invalidate React Query keys so UI auto-refreshes
//   3. Show a toast confirmation
//
type AssistantAction =
  | { type: "navigate"; url: string }                                        // router.push
  | { type: "open_tab"; tab: string }                                        // ?tab=X
  | { type: "refresh"; keys: string[] }                                      // invalidate + refetch
  | { type: "show_toast"; variant: "success" | "error" | "info"; message: string };

// Action tokens can be multi-line JSON — match balanced braces
function extractActions(text: string): { clean: string; actions: AssistantAction[] } {
  const actions: AssistantAction[] = [];
  // Match [action]: followed by a JSON object (handles nested braces)
  const clean = text.replace(/\[action\]:\s*(\{[\s\S]*?\})/g, (_, json) => {
    try { actions.push(JSON.parse(json) as AssistantAction); } catch { /* malformed — ignore */ }
    return "";
  }).trim();
  return { clean, actions };
}

const OPEN_PREF_KEY = "aios-assistant-open";
const CHAT_CACHE_KEY = "aios-assistant-chat";

// The Assistant navigates the user off this page, which unmounts the panel. The
// backend persists an interrupted reply only after the client has disconnected,
// so a refetch on return can arrive before the save lands. Holding the thread
// here makes returning instant and removes that race; the server is still the
// source of truth for a brand-new browser session.
type CachedChat = { messages: ChatMessageProps[]; sessionId: string | null };

function readCachedChat(): CachedChat | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(CHAT_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedChat;
    return Array.isArray(parsed?.messages) ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedChat(chat: CachedChat): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      CHAT_CACHE_KEY,
      JSON.stringify({ ...chat, messages: chat.messages.slice(-50) })
    );
  } catch {
    // storage full or blocked — the server copy still covers a fresh session
  }
}

type AiosAssistantMessage = {
  id?: string;
  created_at?: string;
  updated_at?: string;
  session_id?: string;
  content?: string;
  role?: string;
  attachments?: ChatAttachment[];
};

type AiosAssistantMessagesResponse = {
  item_count?: number;
  page_count?: number;
  items?: AiosAssistantMessage[];
};

function isUserRole(role?: string): boolean {
  const normalized = role?.trim().toUpperCase();
  return normalized === "USER";
}

function mapApiMessagesToChat(items: AiosAssistantMessage[]): ChatMessageProps[] {
  const sorted = [...items].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    return aTime - bTime;
  });

  const mapped: ChatMessageProps[] = [];
  for (const row of sorted) {
    const text = row.content?.trim() ?? "";
    if (!text && !row.attachments?.length) continue;
    const isUser = isUserRole(row.role);
    mapped.push({
      id: row.id,
      message: text,
      isUser,
      timestamp: row.created_at ? new Date(row.created_at) : undefined,
      name: isUser ? undefined : "AI",
      attachments: row.attachments,
    });
  }
  return mapped;
}

async function fetchAssistantMessages(): Promise<{
  messages: ChatMessageProps[];
  sessionId: string | null;
}> {
  const params = new URLSearchParams({
    page: "1",
    limit: "50",
    sort_by: "created_at",
    sort_order: "asc",
  });
  const response = (await fetchData(
    `/api/aios-assistant/messages?${params.toString()}`
  )) as AiosAssistantMessagesResponse;
  const items = response?.items ?? [];
  const sessionId = items.find((item: AiosAssistantMessage) => item.session_id)?.session_id ?? null;

  return {
    messages: mapApiMessagesToChat(items),
    sessionId,
  };
}

export interface ChatBubbleProps {
  /**
   * Initial messages to display in the chat
   */
  initialMessages?: ChatMessageProps[];

  /**
   * Callback when a message is sent
   */
  onSendMessage?: (message: string, attachments?: ChatAttachment[]) => void | Promise<void>;

  /**
   * Title of the chat window
   */
  title?: string;

  /**
   * Position of the chat bubble
   * @default "bottom-right"
   */
  position?: "bottom-right" | "bottom-left";

  /**
   * Additional className for the container
   */
  className?: string;
}

export function ChatBubble({
  initialMessages = [],
  onSendMessage,
  title = "Assistant",
  position = "bottom-right",
  className,
}: ChatBubbleProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { getContextualPrompts, pageContext, openChatRequest } = useChatContext();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessageProps[]>(initialMessages);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [contextualPrompts, setContextualPrompts] = useState(getContextualPrompts());
  // Set once this session's thread has been restored, so a slower server response
  // cannot replace it.
  const hasRestoredRef = useRef(false);

  const {
    data: history,
    isLoading: isHistoryLoading,
    hasPermission: canUseAssistant,
    refetch: refetchHistory,
  } = useProtectedQuery({
    queryKey: ["aios-assistant-messages"],
    queryFn: fetchAssistantMessages,
    requiredPermissions: ["CREATE_AIOS_ASSISTANT_MESSAGE"],
  });

  const setOpenPreference = useCallback((open: boolean) => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(OPEN_PREF_KEY, open ? "1" : "0");
    }
    setIsOpen(open);
  }, []);

  // Update contextual prompts when page changes (pathname only to avoid infinite loops)
  useEffect(() => {
    setContextualPrompts(getContextualPrompts());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when route changes
  }, [pathname]);

  // Keep panel open across refresh when user left it open.
  useEffect(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem(OPEN_PREF_KEY) === "1") {
      setIsOpen(true);
    }
  }, []);

  // Restore this session's thread. Done here rather than in the state initialiser
  // because the component server-renders, where sessionStorage does not exist.
  useEffect(() => {
    const cached = readCachedChat();
    if (!cached?.messages.length) return;
    setMessages(cached.messages);
    setSessionId((prev) => prev ?? cached.sessionId);
    hasRestoredRef.current = true;
  }, []);

  // Opened from elsewhere on the page (Ask the Assistant, Raise a ticket, Reply in the Assistant).
  // Goes through setOpenPreference so it survives the panel unmounting when the
  // Assistant navigates the user to another page.
  useEffect(() => {
    if (!openChatRequest) return;
    setOpenPreference(true);
  }, [openChatRequest?.token, setOpenPreference]);

  // Hydrate from API only when local chat is empty (avoids blink after stream ends).
  useEffect(() => {
    if (!history || isLoading || hasRestoredRef.current) return;
    setMessages((prev) => (prev.length > 0 ? prev : history.messages));
    setSessionId((prev) => prev ?? history.sessionId);
  }, [history, isLoading]);

  // Hold the thread for this browser session so returning after the Assistant
  // navigates the user away does not wait on the server, which saves an
  // interrupted reply only after the client has already disconnected.
  useEffect(() => {
    if (messages.length === 0) return;
    const worthKeeping = messages.filter(
      (message) => message.isUser || message.message?.trim()
    );
    writeCachedChat({ messages: worthKeeping, sessionId });
  }, [messages, sessionId]);

  useEffect(() => {
    if (isOpen) {
      void refetchHistory();
    }
  }, [isOpen, refetchHistory]);

  const streamedTextRef = useRef("");
  // Navigating unmounts this panel, which cuts the stream off mid-sentence and
  // leaves an empty reply bubble. Hold the destination and go once the reply has
  // arrived and been saved.
  const pendingNavigationRef = useRef<string | null>(null);
  // True once this mount has sent a message. While it is set, the local list is
  // ahead of the server and must not be replaced; when it is clear (a fresh mount
  // after the Assistant navigated the user away) the server's list is the truth.
  const hasLocalStreamRef = useRef(false);

  // ── Execute actions returned by the backend ──────────────────────────────
  // Backend tools handle all data mutations. Frontend only navigates + refreshes.
  const executeActions = useCallback(async (actions: AssistantAction[]) => {
    for (const action of actions) {
      try {
        switch (action.type) {
          case "navigate":
            router.push(action.url);
            break;

          case "open_tab":
            router.push(`${pathname}?tab=${action.tab}`);
            break;

          case "refresh":
            // Invalidate each key so React Query re-fetches and UI updates instantly
            action.keys.forEach((key) =>
              queryClient.invalidateQueries({ queryKey: [key] })
            );
            break;

          case "show_toast":
            if (action.variant === "success") toast.success(action.message);
            else if (action.variant === "error") toast.error(action.message);
            else toast.info(action.message);
            break;
        }
      } catch {
        // best-effort — never crash the chat
      }
    }
  }, [router, pathname, queryClient]);

  const resetLocalChat = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    streamedTextRef.current = "";
    hasLocalStreamRef.current = false;
    writeCachedChat({ messages: [], sessionId: null });
    queryClient.setQueryData(["aios-assistant-messages"], {
      messages: [],
      sessionId: null,
    });
  }, [queryClient]);

  const clearSessionMutation = useProtectedMutation({
    mutationFn: () => del("/api/aios-assistant/sessions"),
    requiredPermissions: ["DELETE_AIOS_ASSISTANT_SESSION"],
    onSuccess: () => {
      resetLocalChat();
    },
    onError: (error: unknown) => {
      if ((error as { response?: { status?: number } })?.response?.status === 404) {
        resetLocalChat();
        return;
      }
      toast.error("Failed to clear chat");
    },
    showToast: false,
  });

  const buildUiContext = useCallback(() => {
    const segments = pathname?.split("/").filter(Boolean) ?? [];
    const entityId = segments.length >= 1 ? segments[segments.length - 1] : "";
    const entityType = segments.length >= 2 ? segments[segments.length - 2] : "";
    const currentUrl =
      (typeof window !== "undefined" && window.location?.href) || pathname || "";

    // Derive a human-readable page name from the URL
    const pageNameMap: Record<string, string> = {
      dashboard: "Dashboard",
      users: "Users",
      settings: "Settings",
      billing: "Billing",
      integrations: "Integrations",
      documents: "Documents",
      help: "Help",
      blueprints: "Blueprints",
      leads: "Leads",
      customers: "Customers",
      "sales-agents": "Sales Agent",
      "support-agents": "Support Agent",
      "sales-activity-log": "Sales Activity Log",
      "analyst-agent": "Analyst Agent",
      "computer-use-agent": "Computer Use Agent",
      agents: "Command Agent",
    };
    const pageName = segments.reduce<string>((found, seg) => {
      return found || pageNameMap[seg] || "";
    }, "") || segments[segments.length - 1] || "Unknown";

    const pageData: Record<string, unknown> = {
      page_name: pageName,
      page_type: pageContext.pageType || "unknown",
    };

    // Auto-capture every URL query param (page, tab, search, sort, filters etc.)
    const urlParams: Record<string, string> = {};
    searchParams?.forEach((value, key) => { urlParams[key] = value; });
    if (Object.keys(urlParams).length > 0) pageData.url_params = urlParams;

    // Merge manually-set page context on top (wins over auto-detected)
    if (pageContext.filters && Object.keys(pageContext.filters).length > 0) {
      pageData.filters = pageContext.filters;
    }
    if (pageContext.stats && Object.keys(pageContext.stats).length > 0) {
      pageData.stats = pageContext.stats;
    }
    if (pageContext.activeTab) pageData.active_tab = pageContext.activeTab;
    // Also check URL for tab param if not set manually
    else if (urlParams.tab) pageData.active_tab = urlParams.tab;

    if (pageContext.searchQuery) pageData.search_query = pageContext.searchQuery;
    else if (urlParams.search) pageData.search_query = urlParams.search;

    if (pageContext.tableData) pageData.table_state = pageContext.tableData;
    else if (urlParams.page) pageData.table_state = { currentPage: Number(urlParams.page) };

    if (pageContext.selectedItems?.length) pageData.selected_items = pageContext.selectedItems;
    if (pageContext.dateRange) pageData.date_range = pageContext.dateRange;

    // Collect active React Query cache entries — snapshot of what APIs are loaded on this page
    try {
      const SORT_FIELDS = new Set(["created_at", "updated_at", "name", "title", "email", "fullname", "status", "is_active", "type", "date"]);
      const SORT_ORDERS = new Set(["asc", "desc"]);
      const EXCLUDED = new Set(["aios-assistant-messages", "llm-models", "datasources-with-groups"]);

      const labelParams = (raw: unknown[]): Record<string, unknown> => {
        const result: Record<string, unknown> = {};
        let pageAssigned = false;
        for (const p of raw) {
          if (p === null || p === undefined || p === "") continue;
          if (typeof p === "number" && !pageAssigned && p >= 1 && p <= 9999) {
            result.page = p; pageAssigned = true;
          } else if (typeof p === "string" && SORT_ORDERS.has(p)) {
            result.sort_order = p;
          } else if (typeof p === "string" && SORT_FIELDS.has(p)) {
            result.sort_by = p;
          } else if (typeof p === "boolean") {
            result.is_active = p;
          } else if (typeof p === "string" && p.length > 0) {
            result.search = p;
          } else if (typeof p === "object" && !Array.isArray(p)) {
            Object.assign(result, p);
          } else if (Array.isArray(p) && p.length > 0) {
            result.filters = p;
          }
        }
        return result;
      };

      const allQueries = queryClient.getQueryCache().getAll();
      const activeQueries = allQueries
        .filter((q) => q.state.status === "success" && q.getObserversCount() > 0)
        .map((q) => {
          const key = q.queryKey;
          const resource = Array.isArray(key) ? String(key[0]) : String(key);
          const rawParams = Array.isArray(key) && key.length > 1 ? key.slice(1) : [];
          const labeled = labelParams(rawParams);
          const entry: Record<string, unknown> = { resource };
          if (Object.keys(labeled).length > 0) entry.query_params = labeled;
          const d = q.state.data as any;
          if (d?.item_count != null) entry.total_items = d.item_count;
          else if (d?.page_count != null) entry.total_pages = d.page_count;
          return entry;
        })
        .filter((q) => {
          const r = String(q.resource);
          return !r.startsWith("_gated") && !r.startsWith("kb-page") && !EXCLUDED.has(r);
        });

      if (activeQueries.length > 0) {
        pageData.active_queries = activeQueries;
      }
    } catch {
      // Query cache read is best-effort — never block the message
    }

    return {
      current_url: currentUrl,
      page_name: pageName,
      entity_type: entityType,
      entity_id: entityId,
      page_data: pageData,
    };
  }, [pathname, searchParams, pageContext, queryClient]);

  const handleSendMessage = useCallback(
    async (message: string, attachments?: ChatAttachment[]) => {
      const userMessage: ChatMessageProps = {
        id: `user-${Date.now()}`,
        message,
        isUser: true,
        timestamp: new Date(),
        attachments,
      };

      const assistantMessage: ChatMessageProps = {
        id: `assistant-${Date.now()}`,
        message: "",
        isUser: false,
        timestamp: new Date(),
        name: "AI",
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsLoading(true);
      streamedTextRef.current = "";
      hasLocalStreamRef.current = true;

      try {
        if (onSendMessage) {
          await onSendMessage(message, attachments);
          setIsLoading(false);
          return;
        }

        const body: Record<string, unknown> = {
          content: message,
          ui_context: buildUiContext(),
        };
        if (attachments?.length) {
          body.file_ids = attachments.map((attachment) => attachment.id);
        }
        // The backend does not cache this — it reads route_catalog off each
        // request's ui_context — so sending it only on the first message left
        // every later navigation with an empty catalog ("route list unavailable").
        body.route_catalog = serializeCatalog();
        if (sessionId) {
          body.session_id = sessionId;
        }

        // Buffer for incomplete JSON lines across chunks
        let lineBuffer = "";

        await postStream(
          "/api/aios-assistant/messages",
          body,
          (chunk: string) => {
            lineBuffer += chunk.endsWith("\n") ? chunk : `${chunk}\n`;

            // Split on newlines — each complete line is a JSON event
            const lines = lineBuffer.split("\n");
            // Last element may be incomplete — keep it in the buffer
            lineBuffer = lines.pop() ?? "";

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed) continue;

              // Try to parse as a structured event
              let event: Record<string, unknown> | null = null;
              try { event = JSON.parse(trimmed); } catch { /* plain text chunk — handle below */ }

              if (event) {
                const type = event.type as string;

                if (type === "text_delta") {
                  // ── Only text_delta is ever displayed in the conversation ──
                  streamedTextRef.current += (event.text as string) ?? "";
                  setMessages((prev) => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last && !last.isUser)
                      next[next.length - 1] = { ...last, message: streamedTextRef.current };
                    return next;
                  });

                } else if (type === "navigation") {
                  // Full page navigation — build URL with optional query params
                  const path = event.path as string;
                  const query = event.query as Record<string, string> | undefined;
                  if (path) {
                    const qs = query && Object.keys(query).length
                      ? "?" + new URLSearchParams(query).toString()
                      : "";
                    pendingNavigationRef.current = path + qs;
                  }

                } else if (type === "open_tab") {
                  // Switch tab in-place on the current page — no navigation
                  const tab = event.tab as string;
                  if (tab) void executeActions([{ type: "open_tab", tab }]);

                } else if (type === "data_mutations") {
                  // Backend tool completed — invalidate specified query keys
                  const keys = (event.query_keys as string[]) ?? [];
                  if (keys.length) void executeActions([{ type: "refresh", keys }]);

                } else if (type === "error") {
                  // Outer stream error — show in chat as a message
                  const errMsg = (event.error as any)?.message ?? "Something went wrong.";
                  setMessages((prev) => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last && !last.isUser)
                      next[next.length - 1] = { ...last, message: errMsg };
                    return next;
                  });

                } else if (type === "session_id") {
                  setSessionId(event.session_id as string);
                }
                // All other event types are silently ignored

              } else {
                // Plain text fallback (old format) — keep backward compat
                streamedTextRef.current += trimmed;

                // Strip legacy [session_id] token
                const sessionMatch = streamedTextRef.current.match(/\[session_id\]\s*:\s*"([^"]+)"/);
                if (sessionMatch) {
                  setSessionId(sessionMatch[1]);
                  streamedTextRef.current = streamedTextRef.current
                    .replace(/\[session_id\]\s*:\s*"[^"]+"\s*/, "").trim();
                }

                // Strip legacy [action] tokens
                const { clean, actions } = extractActions(streamedTextRef.current);
                streamedTextRef.current = clean;
                if (actions.length > 0) void executeActions(actions);

                setMessages((prev) => {
                  const next = [...prev];
                  const last = next[next.length - 1];
                  if (last && !last.isUser)
                    next[next.length - 1] = { ...last, message: streamedTextRef.current };
                  return next;
                });
              }
            }
          }
        );
      } catch (error) {
        console.error("Error sending message:", error);
        toast.error("Failed to send message");
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && !last.isUser)
            next[next.length - 1] = { ...last, message: "Sorry, something went wrong. Please try again." };
          return next;
        });
      } finally {
        setIsLoading(false);
        const destination = pendingNavigationRef.current;
        pendingNavigationRef.current = null;
        if (destination) {
          void executeActions([{ type: "navigate", url: destination }]);
        }
        // The Assistant may navigate the user off this page, unmounting the panel
        // mid-stream. Drop the cached list so the next mount refetches instead of
        // restoring a snapshot from before this exchange.
        void queryClient.invalidateQueries({
          queryKey: ["aios-assistant-messages"],
        });
      }
    },
    [onSendMessage, buildUiContext, sessionId, queryClient, executeActions]
  );

  const handleToggle = () => {
    setOpenPreference(!isOpen);
  };

  const handleClose = () => {
    setOpenPreference(false);
  };

  const handleClear = useCallback(() => {
    clearSessionMutation.mutate(undefined);
  }, [clearSessionMutation]);

  const positionClasses = {
    "bottom-right": "bottom-4 right-4 min-[701px]:bottom-6 min-[701px]:right-6",
    "bottom-left": "bottom-4 left-4 min-[701px]:bottom-6 min-[701px]:left-6",
  };

  // Hide chat bubble on auth pages and super-admin routes (after all hooks to satisfy rules of hooks)
  const isAuthPage =
    pathname?.includes("/auth") ||
    pathname?.includes("/login") ||
    pathname?.includes("/complete-login") ||
    pathname?.includes("/request-signup") ||
    pathname?.includes("/forgot-password") ||
    pathname?.includes("/update-forgotten-password");
  const isSuperAdminRoute = pathname?.startsWith("/super-admin");
  if (isAuthPage || isSuperAdminRoute || !canUseAssistant) {
    return null;
  }

  return (
    <>
      {/* The panel covers the whole screen below sm, so the page is dimmed only there. */}
      <div
        aria-hidden
        onClick={handleClose}
        className={cn(
          "fixed inset-0 z-40 bg-[rgba(30,30,30,0.35)] transition-opacity duration-200 min-[701px]:hidden",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      />

      <aside
        id="aios-assistant-panel"
        role="dialog"
        aria-label={title}
        aria-hidden={!isOpen}
        inert={!isOpen}
        className={cn(
          "fixed z-50 flex flex-col overflow-hidden bg-card",
          "transition-[opacity,transform] duration-200 ease-out",
          "inset-x-0 bottom-0 h-[min(640px,85dvh)] rounded-t-2xl border-t border-iai-border",
          "shadow-[0_-8px_40px_rgba(42,32,146,0.14)]",
          "min-[701px]:inset-x-auto min-[701px]:bottom-24 min-[701px]:right-6",
          "min-[701px]:h-[min(600px,calc(100dvh-7.5rem))] min-[701px]:w-[400px]",
          "min-[701px]:origin-bottom-right min-[701px]:rounded-2xl min-[701px]:border",
          "min-[701px]:shadow-[0_16px_48px_rgba(42,32,146,0.18)]",
          isOpen
            ? "translate-y-0 opacity-100 min-[701px]:scale-100"
            : "pointer-events-none translate-y-4 opacity-0 min-[701px]:scale-95",
          className
        )}
      >
        <ChatWindow
          messages={messages}
          onSendMessage={handleSendMessage}
          onClose={handleClose}
          onClear={handleClear}
          isLoading={isLoading}
          isHistoryLoading={isHistoryLoading}
          title={title}
          suggestedPrompts={contextualPrompts}
          prefill={
            openChatRequest?.prefill
              ? { token: openChatRequest.token, text: openChatRequest.prefill }
              : null
          }
        />
      </aside>

      <button
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-controls="aios-assistant-panel"
        className={cn(
          "fixed z-[51] flex size-14 cursor-pointer items-center justify-center rounded-full border-0",
          "bg-iai-purple text-white shadow-aios-purple-lift",
          "transition-transform duration-150 hover:scale-[1.06] active:scale-95",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          isOpen && "max-[700px]:hidden",
          positionClasses[position]
        )}
      >
        {isOpen ? (
          <X className="size-6" strokeWidth={2} />
        ) : (
          <MessageCircle className="size-6" strokeWidth={2} />
        )}
        <span className="sr-only">
          {isOpen ? "Close the Assistant" : "Open the Assistant"}
        </span>
      </button>
    </>
  );
}
